// ===========================================================================
// LOS VIDEOS DEL SITIO, en un solo lugar.
//
// Cada slot de video del sitio —el hero, la muestra de una certificación, la
// de cada curso— pide su pieza por CLAVE. Aquí se declara qué hay detrás de
// cada clave, y solo aquí.
//
// Antes esto vivía como una constante suelta en cada archivo (`VIDEO_ID = ''`
// en quees.js, otra en especializate.js, y en cert.js y curso.js ni siquiera
// eso: el botón de play abría un marco vacío). El resultado era que 43 páginas
// enseñaban un botón que no reproducía nada.
//
// CÓMO SE LLENA UNA ENTRADA
//
//   src  → la URL del archivo propio. `.mp4` o `.webm` los reproduce el
//          navegador solo; `.m3u8` necesita hls.js salvo en Safari (ver
//          player.js). ESTA es la vía buena: reproductor propio, sin marca
//          ajena, sin recomendaciones al final.
//   yt   → ID de YouTube. Puente mientras no hay archivo propio. El reproductor
//          lo detecta y cae al embed de youtube-nocookie.
//   ambos vacíos → NO se dibuja un botón de play. El marco sale con su portada
//          y la leyenda "en preparación". Un botón que no reproduce es un bug;
//          un marco que avisa, no.
//
// La duración se escribe a mano porque es la del corte de la muestra, no la
// del curso: no hay de dónde calcularla.
// ===========================================================================

const POSTER = '/julio-video-poster.jpg';

// EL VIDEO VIVE EN EL CMS, no en el sitio. Las imágenes sí se sirven desde
// aquí —están en public/— pero 93 MB de video no caben razonablemente en un
// repositorio de git.
//
// Octubre 2026: WordPress se mudó de www.zenkai.com.mx a cms.zenkai.com.mx,
// porque www pasó a ser ESTE sitio. La constante apunta ya al destino nuevo.
// Mientras el CMS no responda, los seis marcos salen sin reproducir —el
// reproductor lo maneja— y vuelven solos en cuanto esté, sin tocar una línea.
const ZK = 'https://cms.zenkai.com.mx/wp-content/uploads/2026/10/';

/** Una pieza de video. Todo opcional salvo el título. */
const v = (titulo, { src = null, yt = null, poster = POSTER, duracion = null } = {}) =>
  ({ titulo, src, yt, poster, duracion });

// `claveCurso` se declara arriba porque las portadas de abajo la usan como
// clave calculada: así la clave de cada curso sale de una sola función y no
// de una cadena escrita a mano que pueda separarse del slug.

// Se declara AQUÍ, antes de VIDEOS, y no más abajo con `claveCert`: las
// portadas de curso la usan como clave calculada dentro del propio objeto, y
// `const` no se puede leer antes de su línea. Con la declaración abajo el build
// pasaba sin quejarse y reventaba al ejecutar —"Cannot access 'claveCurso'
// before initialization"—, que es el peor orden posible para enterarse.
export const claveCurso = (slug) => `muestra-${slug}`;
export const claveCert = (slug) => `muestra-cert-${slug}`;

export const VIDEOS = {
  // --- Home ---------------------------------------------------------------
  'julio-historia': v('La historia de Julio Pasos', { yt: 'u4mNDNxZmtw', duracion: '2 min' }),
  'quees': v('¿Qué es Zenkai?', { src: ZK + 'que-es-zenkai-v1-1.2mbps-1.mp4', poster: '/portada-quees.webp', duracion: '3 min 37 s' }),

  // --- Especialízate ------------------------------------------------------
  'tecnica-no-alcanza': v('Por qué la técnica sola no alcanza', { src: ZK + 'especializate-v1-1.2mbps-1.mp4', poster: '/portada-especializate.webp', duracion: '2 min 28 s' }),

  // --- Testimonios --------------------------------------------------------
  // Cuatro reels VERTICALES (9:16), grabados por los propios egresados. Hoy se
  // sirven desde el WordPress viejo: es lo que hay, funciona, y cuando se
  // decida el alojamiento definitivo de video se cambia el `src` de estas
  // cuatro líneas y nada más. Van aquí y no en la sección por la misma razón
  // que todo lo demás: un solo registro para todo el video del sitio.
  'reel-juan-cabezas': v('Juan Cabezas, líder de OPEX', { src: ZK + 'reel-juan-cabezas.mp4', poster: '/reel-juan-cabezas.webp' }),
  'reel-noe': v('Noe, Supply Chain', { src: ZK + 'reel-noe.mp4', poster: '/reel-noe.webp' }),
  'reel-alfonso': v('Alfonso, Manufactura', { src: ZK + 'reel-alfonso.mp4', poster: '/reel-alfonso.webp' }),
  'reel-christian': v('Christian, OPEX', { src: ZK + 'reel-christian.mp4', poster: '/reel-christian.webp' }),

  // --- Portadas de certificación -------------------------------------------
  // Las tres rutas. Igual que las de curso: son MINIATURAS, sin `src`, así que
  // el marco sale con su portada y la leyenda "en preparación" hasta que haya
  // corte de muestra. Los archivos llegaron nombrados por su área temática
  // —"Lean Manufacturing", "six sigma", "Process Validation"— y se asignaron a
  // la CERTIFICACIÓN de cada área, que es el único hueco que existe: las
  // páginas de categoría no piden pieza de video.
  [claveCert('lean-leader')]: v('Lean Leader', { poster: '/portada-cert-lean-leader.webp' }),
  [claveCert('six-sigma-green-belt')]: v('Six Sigma Green Belt', { poster: '/portada-cert-six-sigma-green-belt.webp' }),
  // Cambiada por la del juego nuevo: la anterior mostraba a alguien midiendo
  // una pieza con calibrador —metrología, que es Ingeniería de Calidad— y no
  // la validación. Esta pertenece a la misma familia visual que las nueve
  // portadas de curso de la ruta, así que la certificación y sus cursos se ven
  // como una sola cosa.
  [claveCert('validacion-de-procesos')]: v('Process Validation', { poster: '/portada-cert-validacion-de-procesos.webp' }),

  // --- Portadas de curso ---------------------------------------------------
  //
  // Son MINIATURAS, no videos: ninguna trae `src` ni `yt`, así que `hayVideo`
  // sigue devolviendo falso y el marco sale con su portada y la leyenda "en
  // preparación" —que es la verdad— en vez de un botón que no reproduce nada.
  // Cuando llegue el corte de muestra de un curso, se le agrega `src` a SU
  // línea y esa página empieza a reproducir sola.
  //
  // Las imágenes viven en la Media Library de WordPress y no en `public/`. Dos
  // razones: el paquete del plugin ya rozaba el límite de subida del servidor
  // (nginx devolvía 413) y estas 31 portadas le habrían sumado 2 MB; y así las
  // comparten el sitio estático y la versión incrustada sin duplicarse.
  // Originales en assets/images/, convertidos a WebP: 81.5 MB → 2.2 MB.
  [claveCurso('5s')]: v('5S', { poster: '/portada-5s.webp' }),
  [claveCurso('administrative-process-kaizen')]: v('Administrative Process Kaizen', { poster: '/portada-administrative-process-kaizen.webp' }),
  [claveCurso('agenda-e-introduccion')]: v('Agenda e Introducción al Curso', { poster: '/portada-agenda-e-introduccion.webp' }),
  [claveCurso('analisis-de-datos-continuos')]: v('Análisis de Datos Continuos', { poster: '/portada-analisis-de-datos-continuos.webp' }),
  [claveCurso('anova')]: v('ANOVA', { poster: '/portada-anova.webp' }),
  [claveCurso('buenas-practicas-de-documentacion')]: v('Buenas Prácticas de Documentación', { poster: '/portada-buenas-practicas-de-documentacion.webp' }),
  [claveCurso('buenas-practicas-de-manufactura')]: v('Buenas Prácticas de Manufactura', { poster: '/portada-buenas-practicas-de-manufactura.webp' }),
  [claveCurso('control-estadistico-de-procesos')]: v('Control Estadístico de Procesos', { poster: '/portada-control-estadistico-de-procesos.webp' }),
  [claveCurso('cuartos-limpios')]: v('Cuartos Limpios', { poster: '/portada-cuartos-limpios.webp' }),
  [claveCurso('definicion-de-problemas-y-situacion-actual')]: v('Definición de Problemas y Situación Actual', { poster: '/portada-definicion-de-problemas-y-situacion-actual.webp' }),
  [claveCurso('distribucion-de-la-probabilidad')]: v('Distribución de la Probabilidad', { poster: '/portada-distribucion-de-la-probabilidad.webp' }),
  [claveCurso('estadistica-descriptiva')]: v('Estadística Descriptiva', { poster: '/portada-estadistica-descriptiva.webp' }),
  [claveCurso('estudios-de-concordancia')]: v('Estudios de Concordancia', { poster: '/portada-estudios-de-concordancia.webp' }),
  [claveCurso('flujo-continuo')]: v('Continuous Flow', { poster: '/portada-flujo-continuo.webp' }),
  [claveCurso('hoshin-kanri')]: v('Hoshin Kanri', { poster: '/portada-hoshin-kanri.webp' }),
  [claveCurso('introduccion-a-six-sigma-green-belt')]: v('Introducción a Six Sigma Green Belt', { poster: '/portada-introduccion-a-six-sigma-green-belt.webp' }),
  [claveCurso('introduccion-ingenieria-de-calidad')]: v('Introducción a la Ingeniería de Calidad', { poster: '/portada-introduccion-ingenieria-de-calidad.webp' }),
  [claveCurso('lean-daily-management')]: v('Lean Daily Management', { poster: '/portada-lean-daily-management.webp' }),
  [claveCurso('lean-thinking')]: v('Lean Thinking', { poster: '/portada-lean-thinking.webp' }),
  [claveCurso('lectura-de-planos')]: v('Lectura de Planos', { poster: '/portada-lectura-de-planos.webp' }),
  [claveCurso('lider-de-evento-kaizen')]: v('Líder de Evento Kaizen', { poster: '/portada-lider-de-evento-kaizen.webp' }),
  [claveCurso('minitab')]: v('Minitab', { poster: '/portada-minitab.webp' }),
  [claveCurso('msa')]: v('Análisis del Sistema de Medición (MSA)', { poster: '/portada-msa.webp' }),
  [claveCurso('pfmea')]: v('Desarrollo de pFMEA', { poster: '/portada-pfmea.webp' }),
  [claveCurso('pruebas-de-hipotesis')]: v('Pruebas de Hipótesis', { poster: '/portada-pruebas-de-hipotesis.webp' }),
  [claveCurso('pull-system')]: v('Pull System', { poster: '/portada-pull-system.webp' }),
  [claveCurso('quality-inspection-kaizen')]: v('Quality Inspection Kaizen', { poster: '/portada-quality-inspection-kaizen.webp' }),
  [claveCurso('regresiones')]: v('Regresiones', { poster: '/portada-regresiones.webp' }),
  [claveCurso('set-up-wheel')]: v('Set Up Wheel', { poster: '/portada-set-up-wheel.webp' }),
  [claveCurso('smed')]: v('SMED', { poster: '/portada-smed.webp' }),
  [claveCurso('value-stream-mapping')]: v('Value Stream Mapping', { poster: '/portada-value-stream-mapping.webp' }),

  // --- Validación de Procesos ---------------------------------------------
  // Las nueve que faltaban de esta ruta: eran los únicos cursos del catálogo
  // sin portada y salían con la imagen genérica de Julio. Se sirven desde
  // public/ y no del WordPress como las otras cuarenta: es el mismo archivo,
  // pero por Cloudflare en vez de por WordPress. tools/portadas-validacion.mjs
  // las rehace desde los originales.
  //
  // OJO con `tmv`: su arte es el MISMO que el de pre-validaciones —la misma
  // lista de verificación y la misma tabla de corridas, solo cambia el saco
  // por camisa—. No ilustra la validación de métodos de prueba y los dos
  // cursos van en la misma ruta, uno junto al otro. Se usa para no dejar el
  // marco vacío, pero pide arte propio.
  [claveCurso('analisis-de-causa-raiz')]: v('Análisis de Causa Raíz', { poster: '/portada-analisis-de-causa-raiz.webp' }),
  [claveCurso('etapas-de-validaciones-de-procesos')]: v('Etapas de Validaciones de Procesos', { poster: '/portada-etapas-de-validaciones-de-procesos.webp' }),
  [claveCurso('introduccion-validacion-ambiente-regulado')]: v('Introducción a la Validación en un Ambiente Regulado', { poster: '/portada-introduccion-validacion-ambiente-regulado.webp' }),
  [claveCurso('lean-dentro-del-proceso-de-validacion')]: v('Lean Dentro del Proceso de Validación', { poster: '/portada-lean-dentro-del-proceso-de-validacion.webp' }),
  [claveCurso('manteniendo-el-estado-de-validacion')]: v('Manteniendo el Estado de Validación', { poster: '/portada-manteniendo-el-estado-de-validacion.webp' }),
  [claveCurso('planes-de-control')]: v('Desarrollo de Planes de Control', { poster: '/portada-planes-de-control.webp' }),
  [claveCurso('pre-validaciones-de-procesos')]: v('Pre Validaciones de Procesos', { poster: '/portada-pre-validaciones-de-procesos.webp' }),
  [claveCurso('protocolos-y-reportes-de-validacion')]: v('Protocolos y Reportes de Validación', { poster: '/portada-protocolos-y-reportes-de-validacion.webp' }),
  [claveCurso('tmv')]: v('Test Method Validation', { poster: '/portada-tmv.webp' }),
};

// Las muestras de certificación y de curso se piden con estas claves. Todavía
// no existe ninguna: los 43 marcos salen en "preparación" hasta que Julio pase
// los cortes. Al agregar aquí `muestra-cert-lean-leader` o `muestra-5s`, esa
// página empieza a reproducir sola —no hay que tocar la plantilla—.

/** La pieza detrás de una clave, o null si la clave no está declarada. */
export const videoDe = (clave) => VIDEOS[clave] || null;

/** ¿Hay algo que reproducir? Falso también si la entrada existe pero va vacía. */
export const hayVideo = (clave) => {
  const x = VIDEOS[clave];
  return Boolean(x && (x.src || x.yt));
};

/** La portada a usar, exista o no la entrada. */
export const posterDe = (clave) => VIDEOS[clave]?.poster || POSTER;
